import { jsx as _jsx } from "react/jsx-runtime";
import { useTrackTranscriptSearched } from "@air/analytics/src/events/useTrackTranscriptSearched";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useFetchSearchResultsLogic } from "~/components/AssetModalV2/Content/Panel/Transcript/hooks/useFetchSearchResultsLogic";
import { TranscriptSearchWrapper } from "~/components/AssetModalV2/Content/Panel/Transcript/TranscriptSearchWrapper";
import { prefetchNextSearchResults, prefetchPreviousSearchResults } from "~/components/AssetModalV2/Content/Panel/Transcript/utils";
import { useTranscriptSearchResultsContext } from "~/providers/TranscriptSearchResultsProvider";
import { useVideoTimestampObserverContext } from "~/providers/VideoTimestampObserverProvider";
import { centralizedClipDurationSelector, centralizedClipIdSelector } from "~/store/centralizedClip/selectors";
import { useSearchedItemsForPrivateTranscript } from "~/swr-hooks/clips/transcript/search/useSearchedItemsForPrivateTranscript";
import { useAirSelector, useAirStore } from "~/utils/ReduxUtils";
export const PrivateTranscriptSearch = /*#__PURE__*/ memo(()=>{
    const store = useAirStore();
    const clipId = useAirSelector(centralizedClipIdSelector);
    var _useAirSelector;
    const clipDuration = (_useAirSelector = useAirSelector(centralizedClipDurationSelector)) !== null && _useAirSelector !== void 0 ? _useAirSelector : 0;
    const [searchText, setSearchText] = useState("");
    const { data: searchResults, isFetching, fetchSearchResultsForRange } = useSearchedItemsForPrivateTranscript({
        clipId,
        search: searchText,
        videoDurationMs: clipDuration * 1000
    });
    const { setFetchSearchResultsForRange } = useTranscriptSearchResultsContext();
    useEffect(()=>{
        setFetchSearchResultsForRange(fetchSearchResultsForRange);
    }, [
        fetchSearchResultsForRange,
        setFetchSearchResultsForRange
    ]);
    const { trackTranscriptSearched } = useTrackTranscriptSearched();
    const { getCurrentTime } = useVideoTimestampObserverContext();
    const { fetchInitialSearchResults } = useFetchSearchResultsLogic();
    const fetchInitialSearchResultsRef = useRef(fetchInitialSearchResults);
    fetchInitialSearchResultsRef.current = fetchInitialSearchResults;
    useEffect(()=>{
        var _centralizedClipDurationSelector, _getCurrentTime;
        fetchInitialSearchResultsRef.current({
            searchText,
            fetchSearchResultsForRange,
            clipDurationInMs: ((_centralizedClipDurationSelector = centralizedClipDurationSelector(store.getState())) !== null && _centralizedClipDurationSelector !== void 0 ? _centralizedClipDurationSelector : 0) * 1000,
            videoTime: (_getCurrentTime = getCurrentTime()) !== null && _getCurrentTime !== void 0 ? _getCurrentTime : 0
        });
    }, [
        fetchSearchResultsForRange,
        getCurrentTime,
        searchText,
        store
    ]);
    useEffect(()=>{
        if (searchText) {
            trackTranscriptSearched({
                clip_id: clipId,
                search_term: searchText
            });
        }
    }, [
        clipId,
        searchText,
        trackTranscriptSearched
    ]);
    const onCurrentIndexChanged = useCallback(async (newIndex)=>{
        if (!(searchResults === null || searchResults === void 0 ? void 0 : searchResults.data)) return;
        await prefetchNextSearchResults({
            newIndex,
            searchResults: searchResults.data,
            fetchSearchResultsForRange
        });
        await prefetchPreviousSearchResults({
            newIndex,
            searchResults: searchResults.data,
            fetchSearchResultsForRange
        });
    }, [
        fetchSearchResultsForRange,
        searchResults === null || searchResults === void 0 ? void 0 : searchResults.data
    ]);
    return /*#__PURE__*/ _jsx(TranscriptSearchWrapper, {
        onCurrentIndexChange: onCurrentIndexChanged,
        isLoading: isFetching,
        onSearch: setSearchText,
        results: searchResults !== null && searchResults !== void 0 ? searchResults : undefined
    });
});
PrivateTranscriptSearch.displayName = "PrivateTranscriptSearch";
